import { useCallback, useState } from 'react'
import type { PresignedUploadUrlRequest } from '@/lib/api/features/attachments/api/getPresignedUploadUrl'
import {
  confirmUpload,
  deleteAttachment as deleteAttachmentRest,
  getDownloadUrl,
  getPresignedUploadUrl,
  uploadAttachment,
} from '@/lib/api/features/attachments'
import {
  useAttachmentMutations,
  useAttachments as useAttachmentsRxDB,
} from '@/lib/db/rxdb/hooks'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import { logger } from '@/lib/monitoring/logger'
import { getUploadContentType } from '@/lib/utils/fileMime'

// Toggle between presigned URL upload (true) and direct upload (false)
const USE_PRESIGNED_UPLOAD = true // Presigned URL upload (client uploads directly to R2)

interface UseAttachmentsProps {
  workspaceId: string
  entityType: string
  entityId: string
}

interface UploadProgress {
  attachmentId: string
  fileName: string
  progress: number // 0-100
  status: 'uploading' | 'confirming' | 'completed' | 'error'
  error?: string
}

export const useAttachments = ({
  workspaceId,
  entityType,
  entityId,
}: UseAttachmentsProps) => {
  const isTaskAttachment =
    entityType === ENTITY_TYPES.WORK_ITEM || entityType === 'task'
  const canonicalEntityType = isTaskAttachment ? ENTITY_TYPES.WORK_ITEM : entityType

  // Use RxDB hooks for reactive data
  const { attachments: canonicalAttachments, isLoading: isCanonicalLoading } =
    useAttachmentsRxDB({
      entityType: canonicalEntityType,
      entityId,
      isDeleted: false,
    })
  const { attachments: legacyTaskAttachments, isLoading: isLegacyTaskLoading } =
    useAttachmentsRxDB({
      entityType: isTaskAttachment ? 'task' : '__never__',
      entityId,
      isDeleted: false,
    })
  const { deleteAttachment: deleteAttachmentRxDB } = useAttachmentMutations()

  const [uploadProgress, setUploadProgress] = useState<Array<UploadProgress>>([])

  // Convert RxDB documents to API-compatible Attachment type.
  // Hide records still in PendingUpload (0) or Failed (2) — only fully Uploaded (1)
  // entries should appear. Mid-flight uploads surface via uploadProgress instead.
  const rxdbAttachments = isTaskAttachment
    ? [
        ...canonicalAttachments,
        ...legacyTaskAttachments.filter(
          (legacyAttachment) =>
            !canonicalAttachments.some(
              (attachment) => attachment.id === legacyAttachment.id,
            ),
        ),
      ]
    : canonicalAttachments
  const isLoading =
    isCanonicalLoading || (isTaskAttachment && isLegacyTaskLoading)

  const attachments = rxdbAttachments
    .filter((doc) => doc.uploadStatus === 1)
    .map((doc) => ({
      id: doc.id,
      workspace_id: doc.envId,
      entity_type: doc.entityType,
      entity_id: doc.entityId,
      file_name: doc.fileName,
      file_type: doc.fileType,
      file_size_bytes: doc.fileSizeBytes,
      storage_key: doc.storageKey,
      file_url: doc.fileUrl,
      description: doc.description,
      upload_status: doc.uploadStatus,
      is_deleted: doc.isDeleted,
      created_on: new Date(doc.createdOn).toISOString(),
      created_by: doc.createdBy,
      updated_on: new Date(doc.updatedOn).toISOString(),
      updated_by: doc.updatedBy,
      deleted_on: doc.deletedOn ? new Date(doc.deletedOn).toISOString() : null,
      deleted_by: doc.deletedBy ?? null,
    }))

  // Upload file (supports both presigned URL and direct upload)
  const uploadFile = useCallback(
    async (file: File, description?: string) => {
      const tempId = `temp-${Date.now()}`
      let currentStep = 'init'
      // Set once the backend has created the PendingUpload record. If a later
      // step (R2 PUT, confirm) throws, we use this to clean up the orphan so
      // it does not linger in the attachments list via RxDB sync.
      let createdAttachmentId: string | undefined

      try {
        // Add to upload progress
        setUploadProgress((prev) => [
          ...prev,
          {
            attachmentId: tempId,
            fileName: file.name,
            progress: 0,
            status: 'uploading',
          },
        ])

        if (USE_PRESIGNED_UPLOAD) {
          const uploadContentType = getUploadContentType(file)

          // PRESIGNED URL UPLOAD (requires R2 CORS configuration)
          // Step 1: Get presigned URL
          currentStep = 'Getting presigned URL'
          console.log('[AttachmentUpload] Getting presigned URL', {
            fileName: file.name,
            fileSize: file.size,
            fileType: uploadContentType,
          })
          const request: PresignedUploadUrlRequest = {
            fileName: file.name,
            fileType: uploadContentType,
            fileSizeBytes: file.size,
            description,
          }

          const presignedData = await getPresignedUploadUrl(
            workspaceId,
            canonicalEntityType,
            entityId,
            request,
          )
          createdAttachmentId = presignedData.attachmentId

          setUploadProgress((prev) =>
            prev.map((p) =>
              p.attachmentId === tempId
                ? {
                    ...p,
                    attachmentId: presignedData.attachmentId,
                    progress: 10,
                  }
                : p,
            ),
          )

          // Step 2: Upload file directly to R2
          currentStep = 'Uploading to R2'
          console.log('[AttachmentUpload] Uploading to R2', {
            attachmentId: presignedData.attachmentId,
            uploadUrl: presignedData.uploadUrl,
          })
          const xhr = new XMLHttpRequest()

          await new Promise<void>((resolve, reject) => {
            xhr.upload.addEventListener('progress', (e) => {
              if (e.lengthComputable) {
                const progress = Math.round((e.loaded / e.total) * 80) + 10
                setUploadProgress((prev) =>
                  prev.map((p) =>
                    p.attachmentId === presignedData.attachmentId
                      ? { ...p, progress }
                      : p,
                  ),
                )
              }
            })

            xhr.addEventListener('load', () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve()
              } else {
                console.error(
                  '[AttachmentUpload] R2 upload failed',
                  {
                    status: xhr.status,
                    statusText: xhr.statusText,
                    responseText: xhr.responseText,
                    fileName: file.name,
                  },
                )
                reject(
                  new Error(
                    `Upload to R2 failed with status ${xhr.status} ${xhr.statusText}: ${xhr.responseText || '(no response body)'}`,
                  ),
                )
              }
            })

            xhr.addEventListener('error', () => {
              console.error('[AttachmentUpload] XHR error event', {
                status: xhr.status,
                statusText: xhr.statusText,
                responseText: xhr.responseText,
                fileName: file.name,
              })
              reject(
                new Error(
                  xhr.status === 0
                    ? 'Network error uploading to R2. The browser could not read a response; check the private R2 bucket CORS settings for this web origin.'
                    : `Network error uploading to R2 (status ${xhr.status} ${xhr.statusText}): ${xhr.responseText || '(no response body)'}`,
                ),
              )
            })
            xhr.addEventListener('abort', () => {
              console.warn('[AttachmentUpload] XHR aborted', {
                fileName: file.name,
              })
              reject(new Error('Upload aborted'))
            })

            xhr.open('PUT', presignedData.uploadUrl)
            xhr.setRequestHeader('Content-Type', uploadContentType)
            xhr.send(file)
          })

          // Step 3: Confirm upload
          currentStep = 'Confirming upload'
          console.log('[AttachmentUpload] Confirming upload', {
            attachmentId: presignedData.attachmentId,
          })
          setUploadProgress((prev) =>
            prev.map((p) =>
              p.attachmentId === presignedData.attachmentId
                ? { ...p, progress: 90, status: 'confirming' }
                : p,
            ),
          )

          await confirmUpload(
            workspaceId,
            presignedData.attachmentId,
          )

          setUploadProgress((prev) =>
            prev.map((p) =>
              p.attachmentId === presignedData.attachmentId
                ? { ...p, progress: 100, status: 'completed' }
                : p,
            ),
          )

          // RxDB will sync automatically via WebSocket, no need to fetch

          setTimeout(() => {
            setUploadProgress((prev) =>
              prev.filter(
                (p) => p.attachmentId !== presignedData.attachmentId,
              ),
            )
          }, 2000)

          return presignedData.attachmentId
        } else {
          // DIRECT UPLOAD (through API server - no CORS needed)
          setUploadProgress((prev) =>
            prev.map((p) =>
              p.attachmentId === tempId ? { ...p, progress: 50 } : p,
            ),
          )

          const result = await uploadAttachment(
            workspaceId,
            canonicalEntityType,
            entityId,
            { file, description },
          )

          setUploadProgress((prev) =>
            prev.map((p) =>
              p.attachmentId === tempId
                ? {
                    ...p,
                    attachmentId: result.id,
                    progress: 100,
                    status: 'completed',
                  }
                : p,
            ),
          )

          // RxDB will sync automatically via WebSocket, no need to fetch

          setTimeout(() => {
            setUploadProgress((prev) =>
              prev.filter((p) => p.attachmentId !== result.id),
            )
          }, 2000)

          return result.id
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Upload failed'
        console.error('[AttachmentUpload] Upload pipeline failed', {
          step: currentStep,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          entityType,
          entityId,
          workspaceId,
          error: err,
        })
        logger.logError(err as Error, 'ATTACHMENT_UPLOAD', {
          step: currentStep,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          entityType,
          entityId,
          workspaceId,
        })
        setUploadProgress((prev) =>
          prev.map((p) =>
            p.attachmentId === tempId || p.fileName === file.name
              ? { ...p, status: 'error', error: errorMessage }
              : p,
          ),
        )

        // Clean up the orphan PendingUpload record on the server so the failed
        // attachment does not appear in the list after RxDB pulls it.
        if (createdAttachmentId) {
          try {
            await deleteAttachmentRest(workspaceId, createdAttachmentId)
          } catch (cleanupErr) {
            logger.logError(cleanupErr as Error, 'ATTACHMENT_UPLOAD_CLEANUP', {
              attachmentId: createdAttachmentId,
              fileName: file.name,
              workspaceId,
            })
          }
        }
        throw err
      }
    },
    [workspaceId, entityType, canonicalEntityType, entityId],
  )

  // Delete attachment (uses RxDB soft delete)
  const deleteAttachmentById = useCallback(
    async (attachmentId: string) => {
      try {
        // Use RxDB mutation to soft-delete (sets isDeleted: true)
        await deleteAttachmentRxDB(attachmentId)
      } catch (err) {
        // Error handling
        throw err
      }
    },
    [deleteAttachmentRxDB],
  )

  // Get download URL
  const getAttachmentDownloadUrl = useCallback(
    async (attachmentId: string, expiresIn: number = 3600) => {
      try {
        const data = await getDownloadUrl(
          workspaceId,
          attachmentId,
          expiresIn,
        )
        return data.downloadUrl
      } catch (err) {
        // Error handling
        throw err
      }
    },
    [workspaceId],
  )

  return {
    attachments,
    uploadProgress,
    loading: isLoading,
    error: null, // RxDB handles errors internally
    uploadFile,
    deleteAttachment: deleteAttachmentById,
    getDownloadUrl: getAttachmentDownloadUrl,
  }
}
